import * as React from 'react';
import { Download, Loader2, Mail } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/toast';
import {
  formatEUR,
  useAdminInvoices,
  useAdminInvoiceDownload,
  useResendInvoice,
} from '../../hooks/useInvoices';

export function AdminUserInvoicesTab({ uid }: { uid: string }) {
  const invoices = useAdminInvoices(uid);
  const download = useAdminInvoiceDownload();
  const resend = useResendInvoice();
  const { toast } = useToast();

  async function onDownload(invoiceId: string, kind: 'pdf' | 'xml') {
    try {
      const url = await download.mutateAsync({ uid, invoiceId, kind });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast({
        title: 'Download fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }
  async function onResend(invoiceId: string, invoiceNumber: string) {
    if (!confirm(`Rechnung ${invoiceNumber} erneut versenden?`)) return;
    try {
      await resend.mutateAsync({ uid, invoiceId });
      toast({ title: 'Rechnung versendet' });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rechnungen für diesen Benutzer</CardTitle>
        <CardDescription>
          Alle ausgestellten Dokumente — sortiert nach Datum.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {invoices.isLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
          </div>
        ) : (invoices.data?.items.length ?? 0) === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Keine Rechnungen für diesen Benutzer.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nummer</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead className="text-right">Brutto</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(invoices.data?.items ?? []).map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-mono">{inv.invoiceNumber}</TableCell>
                  <TableCell className="text-xs">
                    {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('de-DE') : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatEUR(inv.totals.grossCents)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        inv.status === 'sent'
                          ? 'success'
                          : inv.status === 'issued'
                            ? 'default'
                            : 'muted'
                      }
                    >
                      {inv.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => void onDownload(inv.id, 'pdf')}
                        disabled={!inv.pdfStoragePath}
                      >
                        <Download className="mr-1 h-3 w-3" /> PDF
                      </Button>
                      {inv.xmlStoragePath && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => void onDownload(inv.id, 'xml')}
                        >
                          <Download className="mr-1 h-3 w-3" /> XML
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => void onResend(inv.id, inv.invoiceNumber)}
                      >
                        <Mail className="mr-1 h-3 w-3" /> Resend
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
